'use client';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import type { WikiTreeNode } from '@akademi/site-builder-runtime';

/**
 * Sidebar tree nav used by both {@code WikiIndexLayout} and
 * {@code WikiDetailLayout}. Honours {@code expandToDepth} (and
 * always expands the active branch).
 */
export interface WikiTreeNavProps {
  tree: WikiTreeNode[];
  activeSlug?: string;
  expandToDepth?: number;
  pageHrefTemplate?: string;
}

function inActiveBranch(node: WikiTreeNode, activeSlug?: string): boolean {
  if (!activeSlug) return false;
  if (node.slug === activeSlug) return true;
  return node.children.some((c) => inActiveBranch(c, activeSlug));
}

export function WikiTreeNav({
  tree,
  activeSlug,
  expandToDepth = 1,
  pageHrefTemplate = '/wiki/{slug}',
}: WikiTreeNavProps) {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <nav aria-label={t('wiki.treeNavLabel')} style={{ padding: 'var(--space-3, 12px) 0' }}>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {tree.map((n) => (
          <TreeNode
            key={n.slug}
            node={n}
            depth={0}
            expandToDepth={expandToDepth}
            activeSlug={activeSlug}
            template={pageHrefTemplate}
          />
        ))}
      </ul>
    </nav>
  );
}

function TreeNode({
  node,
  depth,
  expandToDepth,
  activeSlug,
  template,
}: {
  node: WikiTreeNode;
  depth: number;
  expandToDepth: number;
  activeSlug?: string;
  template: string;
}) {
  const isActive = activeSlug === node.slug;
  const expanded = depth < expandToDepth || inActiveBranch(node, activeSlug);
  return (
    <li style={{ marginTop: depth === 0 ? 8 : 2 }}>
      <Link
        href={template.replace('{slug}', node.slug)}
        style={{
          display: 'block',
          padding: 'var(--space-1-5, 6px) var(--space-2, 8px)',
          borderRadius: 6,
          textDecoration: 'none',
          color: isActive
            ? 'var(--color-brand-700, #1d4ed8)'
            : 'var(--color-text-primary, #18181b)',
          background: isActive ? 'var(--color-brand-50, #eff6ff)' : 'transparent',
          fontWeight: depth === 0 ? 600 : isActive ? 600 : 400,
          fontSize: depth === 0 ? 14 : 13,
          paddingLeft: 8 + depth * 12,
        }}
      >
        {node.title}
      </Link>
      {expanded && node.children.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {node.children.map((c) => (
            <TreeNode
              key={c.slug}
              node={c}
              depth={depth + 1}
              expandToDepth={expandToDepth}
              activeSlug={activeSlug}
              template={template}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
